import React, { useEffect, useState } from 'react';

import {
  Skeleton,
} from '@mui/material';

import LessonDetails from '../../../components/LessonDetails';
import Modal from '../../../components/Modal';
import SkeletonStack from '../../../components/SkeletonStack';
import StudentRow from '../../../components/StudentRow';
import {
  useAddBreakMutation,
  useGetLessonEnrolmentQuery,
  useGetLessonQuery,
  useMarkRollMutation,
  useRemoveAllFutureLessonsMutation,
  useRemoveLessonMutation,
  useUnenrolFromLessonMutation,
  useUnmarkRollMutation,
  useUpdateLessonEnrolmentMutation,
  useUpdateLessonMutation,
} from '../../../services/classes';
import { Lesson } from '../../../services/classes/types';
import { useGetCoursesQuery } from '../../../services/courses';
import { useGetStaffQuery } from '../../../services/staff';
import { useGetStudentQuery } from '../../../services/students';
import { AttendanceType, EnrolmentType } from '../../../types/common';
import {
  getLessonClass,
  getLessonSubject,
} from '../../../utils/common/classes';
import useError from '../../../utils/react/hooks/useError';
import useSuccess from '../../../utils/react/hooks/useSuccess';
import ClassLessonCard from '../ClassLessonCard';

interface LessonStudentRowProps {
  studentId: string,
  lesson: Lesson,
}

const LessonStudentRow = ({ studentId,
  lesson }: LessonStudentRowProps) => {
  const { data: student, isLoading } = useGetStudentQuery({ studentId });
  const [updateLessonEnrolment] = useUpdateLessonEnrolmentMutation();

  const subject = getLessonSubject(lesson);
  const classId = getLessonClass(lesson);

  const { data: lessonEnrolment,
    isLoading: isLessonEnrolmentLoading } = useGetLessonEnrolmentQuery({
    subject,
    classId,
    lesson: lesson.lesson,
    studentId,
  });

  const [unenrolStudent, {
    isError: isUnenrolStudentError,
    error: unenrolStudentError,
    isSuccess: isUnenrolStudentSuccess,
  }] = useUnenrolFromLessonMutation();

  const handleMarkStudent = (id: string, present: boolean) => {
    updateLessonEnrolment({
      subject,
      classId,
      lesson: lesson.lesson,
      studentId: id,
      newAttributes: {
        present,
      },
    });
  };

  const handleToggleStudentOnline = (id: string) => {
    updateLessonEnrolment({
      subject,
      classId,
      lesson: lesson.lesson,
      studentId: id,
      newAttributes: {
        attendanceType: lessonEnrolment?.data.attendanceType === AttendanceType.Online ?
          AttendanceType.InPerson : AttendanceType.Online,
      },
    });
  };

  const handleLessonUnenrol = (id: string) => {
    unenrolStudent({
      subject,
      classId,
      lesson: lesson.lesson,
      studentId: id,
    });
  };

  useError(isUnenrolStudentError, unenrolStudentError);
  useSuccess(isUnenrolStudentSuccess, 'Student unenrolled');

  if (isLoading || isLessonEnrolmentLoading) {
    return (
      <Skeleton />
    );
  }

  if (student?.data && lessonEnrolment?.data) {
    return (
      <StudentRow
        student={student.data}
        editable={!lesson.rollMarked}
        isPresent={lessonEnrolment.data.present}
        isMakeup={lessonEnrolment.data.enrolmentMethod === EnrolmentType.Makeup}
        isOnline={lessonEnrolment.data.attendanceType === AttendanceType.Online}
        onOnlineToggle={handleToggleStudentOnline}
        onMark={handleMarkStudent}
        studentDetailsHref={`/admin/student/${studentId}`}
        makeupHref={`/roll/${subject}/${classId}/${encodeURIComponent(lesson.lesson)}/reschedule/${studentId}`}
        handleLessonUnenrol={handleLessonUnenrol}
      />
    );
  }
  return (
    <div />
  );
};
interface ClassLessonDetailsProps {
  selectedLesson: Lesson;
  onClose: () => void;
}

const ClassLessonDetails = ({
  selectedLesson,
  onClose = () => {},
}: ClassLessonDetailsProps) => {
  const { data: courses, isLoading: isCoursesLoading } = useGetCoursesQuery({});
  const { data: teacher, isLoading: isTeacherLoading } = useGetStaffQuery({
    staffId: selectedLesson.teacher,
  });

  const subject = getLessonSubject(selectedLesson);
  const classId = getLessonClass(selectedLesson);

  const { data: fetchedLesson,
    isLoading: isLessonLoading } = useGetLessonQuery({
    subject, classId, lesson: selectedLesson.lesson,
  });

  const [lesson, setLesson] = useState<Lesson>(selectedLesson);
  const [isEditBreak, setIsEditBreak] = useState(false);

  useEffect(() => {
    if (fetchedLesson) {
      setLesson(fetchedLesson.data);
    }
  }, [fetchedLesson]);

  useEffect(() => {
    setIsEditBreak(false);
  }, [selectedLesson]);

  const [updateLesson] = useUpdateLessonMutation();
  const [deleteLesson, {
    isSuccess: isDeleteLessonSuccess,
    isLoading: isDeleteLessonLoading,
    isError: isDeleteLessonError,
    error: deleteLessonError,
  }] = useRemoveLessonMutation();
  const [addBreak, {
    isLoading: isAddBreakLoading,
    isError: isAddBreakError,
    error: addBreakError,
    isSuccess: isAddBreakSuccess,
  }] = useAddBreakMutation();
  const [markRoll, { isLoading: isMarkRollLoading,
    isError: isMarkRollError,
    error: markRollError }] = useMarkRollMutation();
  const [unmarkRoll, { isLoading: isUnmarkRollLoading,
    isError: isUnmarkRollError,
    error: unmarkRollError }] = useUnmarkRollMutation();

  const [removeAllFutureLessons, { isLoading: isRemoveAllFutureLessonsLoading,
    isSuccess: isRemoveAllFutureLessonsSuccess,
    isError: isRemoveAllFutureLessonsError,
    error: removeAllFutureLessonsError }] = useRemoveAllFutureLessonsMutation();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [removeAllFutureLessonsModalOpen, setRemoveAllFutureLessonsModalOpen] = useState(false);
  const [
    displayUnremovedFutureLessonsModalOpen,
    setDisplayUnremovedFutureLessonsModalOpen,
  ] = useState(false);
  const [removeAllFutureLessonsSuccessMessage, setRemoveAllFutureLessonsSuccessMessage] = useState('');

  const handleSubmitRoll = () => {
    if (!lesson.rollMarked) {
      markRoll({
        subject,
        classId,
        lesson: lesson.lesson,
      });
    } else {
      unmarkRoll({
        subject,
        classId,
        lesson: lesson.lesson,
      });
    }
  };
  const handleRemoveAllFutureLessons = async () => {
    const response = await removeAllFutureLessons({
      subject,
      classId,
      lesson: lesson.lesson,
    });

    if (response && 'data' in response && response.data) {
      setRemoveAllFutureLessonsSuccessMessage(response.data.message);
    }
  };

  const handleCloseUnremovedLessonModal = async () => {
    setRemoveAllFutureLessonsModalOpen(false);
    onClose();
  };

  useEffect(() => {
    if (isAddBreakSuccess) {
      setIsEditBreak(false);
    }
  }, [isAddBreakSuccess]);

  useEffect(() => {
    if (isDeleteLessonSuccess) {
      onClose();
      setDeleteModalOpen(false);
    }
  }, [isDeleteLessonSuccess]);

  useEffect(() => {
    if (isRemoveAllFutureLessonsSuccess) {
      setRemoveAllFutureLessonsModalOpen(false);
      setDisplayUnremovedFutureLessonsModalOpen(true);
    }
  }, [isRemoveAllFutureLessonsSuccess]);

  useError(isMarkRollError, markRollError);
  useError(isUnmarkRollError, unmarkRollError);
  useError(isAddBreakError, addBreakError);
  useError(isDeleteLessonError, deleteLessonError);
  useError(isRemoveAllFutureLessonsError, removeAllFutureLessonsError);

  useSuccess(isAddBreakSuccess, 'Break added');
  useSuccess(isDeleteLessonSuccess, 'Lesson deleted');

  if (isLessonLoading || isCoursesLoading || isTeacherLoading) {
    return (
      <SkeletonStack />
    );
  }

  return (
    <>
      <LessonDetails
        lesson={lesson}
        classDetailsHref={`/admin/class/${getLessonSubject(lesson)}/${getLessonClass(lesson)}`}
        courses={courses?.data ?? []}
        teacher={teacher?.data}
        onClose={onClose}
        toggleRoll={handleSubmitRoll}
        updateLesson={(newAttributes) => updateLesson({
          subject, classId, lesson: selectedLesson.lesson, newAttributes,
        })}
        editBreak={(weeks) => addBreak({
          subject, classId, weeks, startDatetime: lesson.startDatetime,
        })}
        isEditBreakLoading={isAddBreakLoading}
        isEditBreak={isEditBreak}
        onEditBreakToggle={() => setIsEditBreak((prev) => !prev)}
        onDelete={() => setDeleteModalOpen(true)}
        onDeleteAllFutureLessons={() => setRemoveAllFutureLessonsModalOpen(true)}
        isRollLoading={isMarkRollLoading || isUnmarkRollLoading}
        renderStudentRow={(studentId) => (
          <LessonStudentRow
            key={studentId}
            studentId={studentId}
            lesson={lesson}
          />
        )}
      />
      <Modal
        open={deleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        title="Are you sure?"
        description="This will permanently delete this lesson."
        buttons={[
          {
            label: 'Delete',
            onClick: () => deleteLesson({
              subject, classId, lesson: selectedLesson.lesson,
            }),
            color: 'error',
            loading: isDeleteLessonLoading,
          },
        ]}
      />
      <Modal
        open={removeAllFutureLessonsModalOpen}
        handleClose={() => setRemoveAllFutureLessonsModalOpen(false)}
        title="Are you sure?"
        description="This will permanently delete all lessons starting from:"
        buttons={[
          {
            label: 'Delete',
            onClick: handleRemoveAllFutureLessons,
            color: 'error',
            loading: isRemoveAllFutureLessonsLoading,
          },
        ]}
      >
        <ClassLessonCard
          lesson={selectedLesson}
        />
      </Modal>
      <Modal
        open={displayUnremovedFutureLessonsModalOpen}
        handleClose={handleCloseUnremovedLessonModal}
        title="Bulk Deletion Result"
        description={removeAllFutureLessonsSuccessMessage}
      />
    </>
  );
};

export default ClassLessonDetails;
